import { pickBy } from "es-toolkit";

import { RespondentTypeEnum } from "@/generated/gql/graphql";
import { respondentTypeWithSuffix } from "@/models/respondent";

export type FlashParams = {
  flashSuccess?: string;
  flashWarning?: string;
  flashError?: string;
};
export type GenericParams = FlashParams;

export class LegacyRoutes {
  constructor(
    private baseUrl: string,
    private dossierId: string,
  ) {}

  preferencesUrl(params?: GenericParams) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/preferences`, params);
  }

  questionnairesUrl(params?: GenericParams) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/questionnaires`, params);
  }

  questionnairePreviewUrl(questionnaireId: string, params?: GenericParams) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/questionnaires/${questionnaireId}/preview`,
      params,
    );
  }

  autoProtocolsUrl(params?: GenericParams) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/protocol_subscriptions`, params);
  }

  nonResponseUrl(nonResponseId: string) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/non_responses/${nonResponseId}/edit`,
    );
  }

  notificationWordDocUrl(notificationId: string) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/notifications/${notificationId}/download`,
    );
  }

  notificationPrintUrl(notificationId: string) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/notifications/${notificationId}/print`,
    );
  }

  notificationPdfUrl(notificationId: string) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/notifications/${notificationId}/pdf`,
    );
  }

  newProtocolSubscriptionUrl(autoProtocolId: string, respondentId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/protocol_subscriptions/new`, {
      auto_protocol_id: autoProtocolId,
      respondent_id: respondentId,
    });
  }

  protocolSubscriptionUrl(protocolSubscriptionId: string) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/protocol_subscriptions/${protocolSubscriptionId}`,
    );
  }

  outcomesUrl(params?: GenericParams) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/outcomes`, params);
  }

  outcomeUrl(questionnaireId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/outcomes/${questionnaireId}`);
  }

  newReportUrl(reportTemplateId?: string) {
    const params = reportTemplateId
      ? { "report[report_template_id]": reportTemplateId }
      : undefined;

    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/reports/new`, params);
  }

  reportUrl(reportId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/reports/${reportId}`);
  }

  reportPdfUrl(reportId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/reports/${reportId}/pdf`);
  }

  reportPreviewUrl(reportId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/reports/${reportId}/legacy_preview`);
  }

  reportPrintUrl(reportId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/reports/${reportId}/print`);
  }

  researchProjectUrl(researchProjectId: string, params?: GenericParams) {
    return this.urlWithParams(
      `/epd/dossiers/${this.dossierId}/research_projects/${researchProjectId}`,
      params,
    );
  }

  responseUrl(responseId: string) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/answers/${responseId}`);
  }

  startFillOutSessionUrl(respondentType: RespondentTypeEnum) {
    return this.urlWithParams(`/epd/dossiers/${this.dossierId}/fill_out_sessions/start`, {
      respondent_type: respondentTypeWithSuffix(respondentType),
    });
  }

  private urlWithParams(path: string, params?: Record<string, string>) {
    const url = new URL(path, this.baseUrl);
    const urlSearchParams = new URLSearchParams(pickBy(params ?? {}, (v) => !!v) as Record<string, string>);
    url.search = urlSearchParams.toString();

    return url.toString();
  }
}
